import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './BattlebotPortalOverlay.css';

const SLOT_COUNT = 5;

function padPlayedZone(arr) {
  const out = Array.isArray(arr) ? [...arr] : [];
  while (out.length < SLOT_COUNT) out.push(null);
  return out.slice(0, SLOT_COUNT);
}

/**
 * First deployment slot for one deployOrder call (preferred zone, then overflow), mirroring orders/index.js deployOrder.
 * Mutates a shallow copy of playedOrders per zone arrays.
 * CRITICAL: empty slots are only `null` (strict), not `undefined` — see deployOrder for loop `=== null`.
 * Client `== null` would treat holes/undefined as empty and shift the index one slot earlier than the server.
 */
function findNextDeploySlot(po, preferredZone) {
  const otherZone = preferredZone === 'front' ? 'flank' : 'front';
  for (const z of [preferredZone, otherZone]) {
    const row = po[z];
    for (let i = 0; i < row.length; i++) {
      if (row[i] === null) {
        return { zone: z, index: i };
      }
    }
  }
  return null;
}

/**
 * Predicts where strategem 369 will place two Battlebots (order 374) before/without server apply — same sequence as server.
 */
export function predictBattlebotDeploySlots(playedOrders) {
  const po = {
    front: padPlayedZone(playedOrders?.front),
    flank: padPlayedZone(playedOrders?.flank),
  };
  const first = findNextDeploySlot(po, 'front');
  if (!first) return null;
  po[first.zone][first.index] = { id: 374 };
  const second = findNextDeploySlot(po, 'flank');
  if (!second) return null;
  return { first, second };
}

/**
 * After state sync, locate Battlebot units (id 374) on the field — same indices as React slot columns.
 * If multiple Battlebots exist, prefers two deployed on `preferTurnNumber` when provided (this strategem’s units).
 */
export function inferBattlebotSlotsFromPlayedOrders(playedOrders, options = {}) {
  const { preferTurnNumber } = options;
  const found = [];
  for (const z of ['front', 'flank']) {
    const arr = playedOrders?.[z];
    if (!Array.isArray(arr)) continue;
    for (let i = 0; i < arr.length; i++) {
      const u = arr[i];
      if (u && Number(u.id) === 374) {
        found.push({
          zone: z,
          index: i,
          instanceId: u.instanceId,
          deployedTurn: u.deployedTurn,
          playedTurn: u.playedTurn,
        });
      }
    }
  }
  if (found.length === 0) return null;
  const pickTwo = (list) => {
    if (list.length === 2) return list.map(({ zone, index }) => ({ zone, index }));
    if (list.length < 2) return null;
    list.sort((a, b) => {
      const na = Number(a.instanceId);
      const nb = Number(b.instanceId);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return String(a.instanceId || '').localeCompare(String(b.instanceId || ''));
    });
    const two = list.slice(-2);
    return two.map(({ zone, index }) => ({ zone, index }));
  };
  if (found.length === 1) {
    return null;
  }
  if (preferTurnNumber != null) {
    const turnHits = found.filter(
      (x) => x.deployedTurn === preferTurnNumber || x.playedTurn === preferTurnNumber
    );
    const twoFromTurn = pickTwo(turnHits);
    if (twoFromTurn) return twoFromTurn;
  }
  return pickTwo(found);
}

/**
 * Maps caster-relative zones (front / flank) to viewer DOM zones (your_* / opponent_*).
 */
export function mapPlayerSlotsToViewer(slotA, slotB, casterIsViewer) {
  const prefix = casterIsViewer ? 'your' : 'opponent';
  return [
    { zone: `${prefix}_${slotA.zone}`, index: slotA.index },
    { zone: `${prefix}_${slotB.zone}`, index: slotB.index },
  ];
}

/**
 * Portal placement uses only deployed units (id 374) after sync — never pre-deploy prediction (was consistently one slot off).
 */
export function resolveBattlebot369ViewerSlots(playedOrders, casterIsViewer, options = {}) {
  const inferred = inferBattlebotSlotsFromPlayedOrders(playedOrders, options);
  if (inferred && inferred.length === 2) {
    return mapPlayerSlotsToViewer(inferred[0], inferred[1], casterIsViewer);
  }
  return null;
}

/**
 * Center of a deployment slot for VFX: battlefield-scoped, visible node, card bounds when present (matches unit art).
 */
export function getBattlebotSlotCenterPixels(zoneName, slotIndex) {
  if (typeof document === 'undefined') return null;
  const idx = String(slotIndex);
  // Document-wide: desktop + mobile often duplicate slots; hidden copies live under .battlefield-with-cross
  // while the visible row may be rendered elsewhere — pick the largest visible rect.
  const candidates = document.querySelectorAll(
    `[data-slot-zone="${zoneName}"][data-slot-index="${idx}"]`
  );
  let bestEl = null;
  let bestArea = -1;
  candidates.forEach((el) => {
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    const area = r.width * r.height;
    if (area > bestArea) {
      bestArea = area;
      bestEl = el;
    }
  });
  if (!bestEl) return null;
  const card = bestEl.querySelector?.('.order-design-card-bounds');
  if (card) {
    const cr = card.getBoundingClientRect();
    if (cr.width > 0 && cr.height > 0) {
      return { x: cr.left + cr.width / 2, y: cr.top + cr.height / 2 };
    }
  }
  const r = bestEl.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/**
 * Center of a hand carousel card (desktop or mobile) for VFX.
 */
export function getHandCarouselCardCenterPixels(instanceId) {
  if (typeof document === 'undefined' || instanceId == null) return null;
  const id = String(instanceId);
  const selectors = [
    `.carousel-order-card[data-instance-id="${id}"]`,
    `.carousel-order-card-mobile[data-instance-id="${id}"]`,
  ];
  let bestEl = null;
  let bestArea = -1;
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const area = r.width * r.height;
      if (area > bestArea) {
        bestArea = area;
        bestEl = el;
      }
    });
  });
  if (!bestEl) return null;
  const card = bestEl.querySelector?.('.order-design-card-bounds');
  if (card) {
    const cr = card.getBoundingClientRect();
    if (cr.width > 0 && cr.height > 0) {
      return { x: cr.left + cr.width / 2, y: cr.top + cr.height / 2 };
    }
  }
  const r = bestEl.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/**
 * Strategem 52 — one portal on the hand slot where the copied order appears (same look as Battlebot portal).
 */
export function FleshMimicHandPortalOverlay({ copiedOrderInstanceId, onComplete }) {
  const [point, setPoint] = useState(null);

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = (attempt) => {
      const pt = getHandCarouselCardCenterPixels(copiedOrderInstanceId);
      if (pt) {
        if (!cancelled) setPoint(pt);
        return;
      }
      if (attempt < 24 && !cancelled) {
        requestAnimationFrame(() => {
          if (!cancelled) measure(attempt + 1);
        });
      }
    };
    setPoint(null);
    measure(0);
    return () => {
      cancelled = true;
    };
  }, [copiedOrderInstanceId]);

  useLayoutEffect(() => {
    if (!point) return undefined;
    const id = window.setTimeout(() => {
      if (typeof onComplete === 'function') onComplete();
    }, 720);
    return () => window.clearTimeout(id);
  }, [point, onComplete]);

  if (!point) {
    return null;
  }

  const disks = (
    <div className="flesh-mimic-portal-root" aria-hidden>
      <div
        className="flesh-mimic-portal-ring"
        style={{ left: `${point.x}px`, top: `${point.y}px` }}
      />
      <div
        className="flesh-mimic-portal-disk"
        style={{ left: `${point.x}px`, top: `${point.y}px` }}
      />
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(disks, document.body);
  }
  return disks;
}

/**
 * Strategem 369 — two portals on the exact slots where Battlebots deploy.
 * @param {{ zone: string, index: number }[]} slots — viewer-relative zones, length 2
 */
export function BattlebotPortalOverlay({ slots, onComplete }) {
  const [points, setPoints] = useState(null);

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = (attempt) => {
      if (!slots || slots.length !== 2) return;
      const pts = slots.map((s) => getBattlebotSlotCenterPixels(s.zone, s.index));
      if (pts.every((p) => p)) {
        if (!cancelled) setPoints(pts);
        return;
      }
      if (attempt < 14 && !cancelled) {
        requestAnimationFrame(() => {
          if (!cancelled) measure(attempt + 1);
        });
      }
    };
    setPoints(null);
    measure(0);
    return () => {
      cancelled = true;
    };
  }, [slots]);

  useLayoutEffect(() => {
    if (!points || points.length !== 2 || !points.every(Boolean)) return undefined;
    const id = window.setTimeout(() => {
      if (typeof onComplete === 'function') onComplete();
    }, 720);
    return () => window.clearTimeout(id);
  }, [points, onComplete]);

  if (!points || points.length !== 2 || !points.every(Boolean)) {
    return null;
  }

  const disks = (
    <div className="battlebot-portal-root" aria-hidden>
      {points.map((pt, i) => (
        <React.Fragment key={i}>
          <div
            className="battlebot-portal-ring"
            style={{ left: `${pt.x}px`, top: `${pt.y}px`, animationDelay: i === 1 ? '0.04s' : '0s' }}
          />
          <div
            className="battlebot-portal-disk"
            style={{ left: `${pt.x}px`, top: `${pt.y}px`, animationDelay: i === 1 ? '0.04s' : '0s' }}
          />
        </React.Fragment>
      ))}
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(disks, document.body);
  }
  return disks;
}

/**
 * Strategems 58 (Plague Bloom), 61 (Cradle of Decay), 184 (Orbital Riftstrike): same motion as Flesh Mimic on deploy slots.
 * `portalVariant`: `'crimson'` (default) or `'void'` (black, for 184).
 * @param {{ zone: string, index: number }[]} slots — viewer-relative zones
 */
export function PlagueBloomSlotPortalsOverlay({ slots, onComplete, portalVariant = 'crimson' }) {
  const [points, setPoints] = useState(null);

  useLayoutEffect(() => {
    if (!slots?.length) {
      setPoints(null);
      return undefined;
    }
    let cancelled = false;
    const measure = (attempt) => {
      const pts = slots.map((s) => getBattlebotSlotCenterPixels(s.zone, s.index));
      if (pts.every(Boolean)) {
        if (!cancelled) setPoints(pts);
        return;
      }
      if (attempt < 22 && !cancelled) {
        requestAnimationFrame(() => {
          if (!cancelled) measure(attempt + 1);
        });
      } else if (!cancelled) {
        setPoints(pts.filter(Boolean));
      }
    };
    setPoints(null);
    measure(0);
    return () => {
      cancelled = true;
    };
  }, [slots]);

  useLayoutEffect(() => {
    if (!points?.length) return undefined;
    const delayMs = 720 + Math.min(points.length, 8) * 48;
    const id = window.setTimeout(() => {
      if (typeof onComplete === 'function') onComplete();
    }, delayMs);
    return () => window.clearTimeout(id);
  }, [points, onComplete]);

  if (!points?.length) {
    return null;
  }

  const isVoid = portalVariant === 'void';
  const diskClass = isVoid ? 'flesh-mimic-portal-disk flesh-mimic-portal-disk--void' : 'flesh-mimic-portal-disk';
  const ringClass = isVoid ? 'flesh-mimic-portal-ring flesh-mimic-portal-ring--void' : 'flesh-mimic-portal-ring';

  const disks = (
    <div className="flesh-mimic-portal-root" aria-hidden>
      {points.map((pt, i) => (
        <React.Fragment key={i}>
          <div
            className={ringClass}
            style={{
              left: `${pt.x}px`,
              top: `${pt.y}px`,
              animationDelay: `${i * 0.055}s`,
            }}
          />
          <div
            className={diskClass}
            style={{
              left: `${pt.x}px`,
              top: `${pt.y}px`,
              animationDelay: `${i * 0.055}s`,
            }}
          />
        </React.Fragment>
      ))}
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(disks, document.body);
  }
  return disks;
}
